import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ConfirmationDialogService } from '../../../shared/services/confirmation-dialog.service';
import {
    selectAudioLabel,
    selectDetailMediaSelection,
    selectDetailPlayerMediaCut,
    selectDetailSubtitleTranscript,
    selectDisplayOtonOnly,
    selectLoadingSubtitleTranscriptTab,
} from '../../details-state/details.selectors';
import { Observable, of, Subject, takeUntil, catchError, map, combineLatest, take, filter, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import {
    clearHoveredTimeFrame,
    getDetailItemSubtitleTranscript,
    getDetailProgramSubtitleTranscript,
    setHoveredTimeFrame,
} from '../../details-state/details.actions';
import { parseTimeSpan } from '../../../shared/timespan';
import { TimeService } from '../../shared/services/time.service';
import { TypedSubtitleTranscriptLineDto, TypedSubtitleTranscriptMetadataDto } from '../../shared/typed_metadata.model';
import { Duration } from '../../../shared/duration';
import { KeyframesService } from '@faro/metadata-angular-client';
import { StoreUtilsService } from '../../../state/store-utils.service';
import {
    OrderRemarkDialogContentAndConfig,
    OrderRemarkResponse,
} from '../../../shared/dialogs/order-remark-dialog/order-remark-dialog-content-and.config';
import { focusElement } from '../../../shared/focus-element.helper';
import { OrderParamService } from '../../shared/services/order-param.service';
import { AddShoppingCartEntryRequestDto } from '@faro/order-angular-client';
import { accessibilityDurationString } from '../../../shared/accessibility-duration.helper';
import { ActionProviderService } from 'src/app/shared/services/action-provider.service';
import { HostConnectorService } from 'src/app/shared/services/host-connector.service';
import { selectCanUseShoppingCart } from 'src/app/state/user.selectors';

@Component({
  standalone: false,
    selector: 'app-subtitle-transcript-content',
    templateUrl: './subtitle-transcript-content.component.html',
    styleUrls: ['./subtitle-transcript-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubtitleTranscriptContentComponent implements OnInit, OnDestroy {
    @Input()
    itemId: string | undefined;

    @Input()
    ccId: string | undefined;

    @Input()
    programId: string = '';

    @Input()
    contentType: string = '';

    @Input()
    readonly: boolean = false;

    @Input()
    keywords: string[] = [];

    loadingSubtitleTranscriptTab$: Observable<boolean>;
    currentVtc$: Observable<Duration | null>;

    highlightedRowIndex: number | undefined;
    canUseShoppingCart$: Observable<boolean>;

    activeSubtitleTranscriptLine: string = '';
    subtitleTranscriptEntryIndex: number = -1;

    tableHeader = [
        { value: 'Medien', scssClass: 'media-header' },
        { value: 'Transkript/Kontextualisierung', scssClass: 'subtitle-transcript-header' },
    ];

    tableRows: TypedSubtitleTranscriptMetadataDto[] = [];

    private _destroyed$ = new Subject<void>();

    get isAppInWebViewContainer(): boolean {
        return this.hostConnector.isAppInWebViewContainer();
    };

    constructor(
        private readonly confirmationDialogService: ConfirmationDialogService,
        private readonly keyframeService: KeyframesService,
        private readonly storeUtils: StoreUtilsService,
        private readonly orderParamService: OrderParamService,
        private readonly actionProviderService: ActionProviderService,
        private readonly hostConnector: HostConnectorService,
        private store: Store,
        private timeService: TimeService,
        private cdr: ChangeDetectorRef,
        private zone: NgZone
    ) {
        this.loadingSubtitleTranscriptTab$ = this.store.select(selectLoadingSubtitleTranscriptTab);
        this.canUseShoppingCart$ = this.store.select(selectCanUseShoppingCart);
        this.currentVtc$ = this.timeService.currentVtc$;
    }

    ngOnInit() {
        this.zone.runOutsideAngular(() => {
            this.timeService.currentVtc$
                .pipe(takeUntil(this._destroyed$))
                .subscribe(currentVtc => {
                    const subtitleTranscriptEntryIndex = this.getEntryIndexFromVtc(currentVtc, this.tableRows);
                    const subtitleTranscriptEntry = this.tableRows[subtitleTranscriptEntryIndex];

                    let subtitleTranscriptLine = '';
                    if (subtitleTranscriptEntry) {
                        const subtitleTranscriptLineEntry =
                            subtitleTranscriptEntry.lines[
                            this.getEntryIndexFromVtc(currentVtc, subtitleTranscriptEntry.lines)
                            ];
                        subtitleTranscriptLine = subtitleTranscriptLineEntry?.text || '';
                    }

                    if (this.subtitleTranscriptEntryIndex !== subtitleTranscriptEntryIndex) {
                        this.subtitleTranscriptEntryIndex = subtitleTranscriptEntryIndex;
                        this.cdr.detectChanges();

                        setTimeout(() => {
                            const activeRow = document.querySelector('tr.active-sequence-transcript-subtitle');
                            if (activeRow) {
                                activeRow.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                            }
                        }, 100);
                    }

                    if (this.activeSubtitleTranscriptLine !== subtitleTranscriptLine) {
                        this.activeSubtitleTranscriptLine = subtitleTranscriptLine;
                        this.cdr.detectChanges();
                    }
                });
        });

        this.store
            .select(selectDetailMediaSelection)
            .pipe(takeUntil(this._destroyed$))
            .subscribe(data => {
                if (data.mediaCut) {
                    if (this.contentType === 'item') {
                        this.store.dispatch(
                            getDetailItemSubtitleTranscript({
                                programId: this.programId,
                                itemId: this.itemId!,
                                mediaCut: data.mediaCut,
                            })
                        );
                    } else {
                        this.store.dispatch(
                            getDetailProgramSubtitleTranscript({ programId: this.programId, mediaCut: data.mediaCut })
                        );
                    }
                }
            });

        combineLatest([
            this.store.select(selectDetailSubtitleTranscript),
            this.store.select(selectDisplayOtonOnly)
        ])
            .pipe(
                takeUntil(this._destroyed$),
                map(([content, filterOton]: [TypedSubtitleTranscriptMetadataDto[] | undefined, boolean]): TypedSubtitleTranscriptMetadataDto[] => {
                    let tableRows = (content || []).map(s => ({
                        ...s,
                        ratingsString: s.ratings?.join('; '),
                        isOton: s.ratings?.includes('O-Ton') || false,
                        hasNonOtonRatings: s.ratings?.some(r => r !== 'O-Ton') || false,
                        length: s.length.split('.')[0] ?? '0',
                        duration: parseTimeSpan(s.length),
                        keyframeBlob$: this.getKeyframeBlob(s.closedCaptionKeyframeId),
                    }));

                    // Apply filter based on the filter setting
                    if (filterOton) {
                        tableRows = tableRows.filter(row => row.isOton);
                    }

                    return tableRows;
                }),
                switchMap((tableRows) => {
                    this.tableRows = tableRows;
                    
                    // if ccId is set, navigate to that closed caption as soon as the player has loaded the mediacut
                    if (this.ccId) {
                        return this.store.select(selectDetailPlayerMediaCut).pipe(
                            filter(data => !!data),
                            take(1),
                            map(() => {
                                const row = this.tableRows.find(row => row.ccId === this.ccId);
                                if (row) {
                                    this.navigateToSequence(row);
                                }
                            })
                        );
                    }
                    
                    // If no ccId, return empty observable to complete the chain
                    return of(null);
                })
            )
            .subscribe(() => {
                this.cdr.detectChanges();
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    setHoveredTimeFrame(sequence: TypedSubtitleTranscriptMetadataDto) {
        this.store.dispatch(
            setHoveredTimeFrame({
                hoveredVtcIn: parseTimeSpan(sequence.vtcIn),
                hoveredVtcOut: parseTimeSpan(sequence.vtcOut),
            })
        );
    }

    clearHoveredTimeFrame(): void {
        this.store.dispatch(clearHoveredTimeFrame());
    }

    getEntryIndexFromVtc(
        currentVtc: Duration | null,
        entryArray: TypedSubtitleTranscriptMetadataDto[] | TypedSubtitleTranscriptLineDto[]
    ): number {
        if (!currentVtc) return -1;

        const currentVtcMs = currentVtc.asMilliseconds();

        return entryArray.findIndex(
            (subtitleTranscript: TypedSubtitleTranscriptMetadataDto | TypedSubtitleTranscriptLineDto) => {
                return (
                    currentVtcMs >= subtitleTranscript.vtcInDuration.asMilliseconds() &&
                    currentVtcMs < subtitleTranscript.vtcOutDuration.asMilliseconds()
                );
            }
        );
    }

    showActions(event: Event, index: number): void {
        event.stopPropagation();
        this.highlightedRowIndex = index;
    }

    jumpToTimeCode(event: Event, timeCode: TypedSubtitleTranscriptLineDto) {
        event.stopPropagation();
        const vtcIn = parseTimeSpan(timeCode.vtcIn);
        this.timeService.jumpToVtc(vtcIn);
    }

    async orderWithRemark(event: Event, entry: TypedSubtitleTranscriptMetadataDto, id: string) {
        event.stopPropagation();
        const audioLabel = await this.storeUtils.snapshot(selectAudioLabel);
        const selectedMedia = await this.storeUtils.snapshot(selectDetailMediaSelection);
        const selectionIds = await this.orderParamService.getSelectionIds();
        const selectedContent: OrderRemarkDialogContentAndConfig = {
            audioTrack: audioLabel,
            displayCheckBox: true,
        };
        this.confirmationDialogService
            .openRemarkDialog(selectedContent)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((data: OrderRemarkResponse) => {
                if (data) {
                    const item: AddShoppingCartEntryRequestDto = {
                        itemId: selectionIds.itemId,
                        programId: selectionIds.programId,
                        ccId: entry.ccId,
                        vtcIn: entry.vtcIn,
                        vtcOut: entry.vtcOut,
                        selectedAudioTrack: data.orderAudioOnly ? selectedMedia.audioTrack : undefined,
                        mediaCutId: data.orderAudioOnly ? selectedMedia.mediaCut : undefined,
                        remark: data.orderRemark,
                        ignoreUserRestrictions: false,
                        ignoreSizeLimit: false,
                    };
                    this.actionProviderService.dispatchShoppingCartAction(item);
                }
                focusElement(id);
            });
    }

    async addToShoppingCart(event: Event, entry: TypedSubtitleTranscriptMetadataDto, id: string) {
        event.stopPropagation();
        const selectionIds = await this.orderParamService.getSelectionIds();
        const item: AddShoppingCartEntryRequestDto = {
            itemId: selectionIds.itemId,
            programId: selectionIds.programId,
            ccId: entry.ccId,
            vtcIn: entry.vtcIn,
            vtcOut: entry.vtcOut,
            ignoreUserRestrictions: false,
            ignoreSizeLimit: false,
        };
        this.actionProviderService.dispatchShoppingCartAction(item);
        focusElement(id);
    }

    getKeyframeBlob(keyframeId: string | null | undefined): Observable<Blob | null> {
        return keyframeId && keyframeId !== '00000000-0000-0000-0000-000000000000'
            ? this.keyframeService.keyframesGetForClosedCaption(keyframeId).pipe(catchError(_ => of(null)))
            : of(null);
    }

    navigateToSequence(sequence: TypedSubtitleTranscriptMetadataDto): void {
        const vtcIn = parseTimeSpan(sequence.vtcIn);
        try {
            this.timeService.jumpToVtc(vtcIn);
        } catch (_) {
            // silently ignore this error - might occur if the current media cut does not "contain" all sequences
        }
    }

    getAccessibilityDurationString(duration: Duration): string {
        return accessibilityDurationString(duration);
    }
}
